import { useState, useEffect, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Spinner, Surface, CopyButton, EmptyState } from "@/components/common";
import { useLocale } from "@/context/LocaleContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FolderOpen, Search, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const MODULE_LABELS = { linkedin: "LinkedIn", debate: "Debatindlæg", newsletter: "Nyhedsbrev", press: "Presse", campaign: "Kampagne", brand: "Brandfortælling", lead: "Leadgenerator" };
const FILTERS = [
  { value: "all", label: "Alle" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "debate", label: "Debatindlæg" },
  { value: "newsletter", label: "Nyhedsbrev" },
  { value: "press", label: "Presse" },
  { value: "campaign", label: "Kampagne" },
  { value: "brand", label: "Brandfortælling" },
  { value: "lead", label: "Leadgenerator" },
];

function fmt(dt) { try { return new Date(dt).toLocaleDateString("da-DK", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } }

export default function Projects() {
  const { t } = useLocale();
  const [projects, setProjects] = useState(null);
  const [q, setQ] = useState("");
  const [module, setModule] = useState("all");
  const [open, setOpen] = useState(null);
  const [edited, setEdited] = useState({});

  const load = useCallback(async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (module !== "all") params.module = module;
      const { data } = await api.get("/projects", { params });
      setProjects(data.projects);
    } catch (err) { toast.error(apiError(err)); setProjects([]); }
  }, [q, module]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openProject = (p) => { setOpen(p); setEdited({ ...p.content }); };

  const save = async () => {
    try {
      await api.put(`/projects/${open.id}`, { content: edited });
      toast.success(t("Gemt"));
      setOpen(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id, e) => {
    e?.stopPropagation();
    try { await api.delete(`/projects/${id}`); toast.success(t("Slettet")); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const contentToText = (c) => Object.values(c || {}).filter((v) => typeof v === "string").join("\n\n");

  return (
    <div>
      <PageHeader overline="Gemte projekter" title="Alt jeres indhold ét sted"
        subtitle="Alle genererede tekster gemmes automatisk. Søg, åbn, redigér, kopiér eller slet." />

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Søg i projekter...")} className="pl-9" data-testid="projects-search" />
        </div>
        <Select value={module} onValueChange={setModule}>
          <SelectTrigger className="sm:w-48" data-testid="projects-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{FILTERS.map((f) => <SelectItem key={f.value} value={f.value}>{t(f.label)}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {projects === null ? <Spinner label="Henter projekter..." /> :
        projects.length === 0 ? <EmptyState icon={FolderOpen} title="Ingen gemte projekter endnu" subtitle="Alt du genererer, dukker op her." testid="projects-empty" /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="projects-list">
            {projects.map((p, i) => (
              <Surface key={p.id} delay={i * 0.03} testid={`project-${p.id}`} className="cursor-pointer group" >
                <div onClick={() => openProject(p)}>
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="secondary" className="text-xs">{t(MODULE_LABELS[p.module] || p.module)}</Badge>
                    <span className="text-xs text-muted-foreground font-mono">{fmt(p.created_at)}</span>
                  </div>
                  <h3 className="font-display font-medium leading-snug line-clamp-1">{p.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2 leading-relaxed">{contentToText(p.content).slice(0, 160)}</p>
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <Button size="sm" variant="secondary" onClick={() => openProject(p)} className="gap-2" data-testid={`project-open-${p.id}`}><Pencil className="h-3.5 w-3.5" /> {t("Åbn")}</Button>
                  <CopyButton text={contentToText(p.content)} testid={`project-copy-${p.id}`} />
                  <Button size="sm" variant="ghost" onClick={(e) => remove(p.id, e)} className="ml-auto text-muted-foreground hover:text-destructive" data-testid={`project-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </Surface>
            ))}
          </div>
        )}

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="project-dialog">
          <DialogHeader><DialogTitle className="font-display">{open?.title}</DialogTitle></DialogHeader>
          {open && (
            <div className="space-y-4">
              {Object.entries(edited).filter(([, v]) => typeof v === "string").map(([k, v]) => (
                <div key={k} className="space-y-1.5">
                  <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{k}</label>
                  <Textarea value={v} onChange={(e) => setEdited({ ...edited, [k]: e.target.value })} rows={v.length > 200 ? 6 : 2} data-testid={`project-edit-${k}`} />
                </div>
              ))}
              <div className="flex gap-2 pt-2">
                <Button onClick={save} data-testid="project-save-btn">{t("Gem ændringer")}</Button>
                <CopyButton text={contentToText(edited)} testid="project-dialog-copy" />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
